import { useState, useEffect } from 'react';
import {
  Box, Button, Stack, Typography, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Chip, Autocomplete,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, Block as AnularIcon, Visibility as VerIcon,
  Edit as EditIcon, PostAdd as ReprogramarIcon, Save as SaveIcon, Close as CancelIcon,
} from '@mui/icons-material';
import { useBeneficiosStore } from '../store/beneficiosStore';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { Prestamo, PrestamoCuota } from '../types/beneficios';

const initialPrestamo: Partial<Prestamo> = {
  idPrestamo: 0,
  idTrabajador: 0,
  tipoPrestamo: 'ADELANTO',
  descripcion: '',
  montoOriginal: 0,
  numeroCuotas: 1,
  fechaInicio: new Date().toISOString().split('T')[0],
  observaciones: '',
};

export default function PrestamosPanel() {
  const { prestamos, cuotasSeleccionadas, loading, loadPrestamos, crearPrestamo, anularPrestamo, loadCuotas, updateCuota, reprogramarCuotas } = useBeneficiosStore();
  const [openNew, setOpenNew] = useState(false);
  const [openCuotas, setOpenCuotas] = useState(false);
  const [formData, setFormData] = useState(initialPrestamo);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState<Trabajador | null>(null);
  const [currentPrestamoId, setCurrentPrestamoId] = useState<number>(0);
  const [editingCuotaId, setEditingCuotaId] = useState<number | null>(null);
  const [editMonto, setEditMonto] = useState<number>(0);
  const [editFecha, setEditFecha] = useState<string>('');
  const [openReprogramar, setOpenReprogramar] = useState(false);
  const [cuotasAdicionales, setCuotasAdicionales] = useState<number>(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadPrestamos();
    TrabajadorService.getAll().then(data => setTrabajadores(data.filter(t => t.activo))).catch(() => {});
  }, []);

  const handleCrear = async () => {
    await crearPrestamo(formData as Prestamo);
    setOpenNew(false);
    setFormData(initialPrestamo);
    setSelectedTrabajador(null);
  };

  const handleVerCuotas = (idPrestamo: number) => {
    setCurrentPrestamoId(idPrestamo);
    loadCuotas(idPrestamo);
    setEditingCuotaId(null);
    setOpenCuotas(true);
  };

  const handleEditCuota = (cuota: PrestamoCuota) => {
    setEditingCuotaId(cuota.idPrestamoCuota);
    setEditMonto(cuota.monto);
    setEditFecha(cuota.fechaProgramada?.substring(0, 10) ?? '');
  };

  const handleSaveCuota = async () => {
    if (!editingCuotaId) return;
    await updateCuota(editingCuotaId, editMonto, editFecha, currentPrestamoId);
    setEditingCuotaId(null);
  };

  const handleReprogramar = async () => {
    if (!currentPrestamoId || cuotasAdicionales < 1) return;
    await reprogramarCuotas(currentPrestamoId, cuotasAdicionales);
    setOpenReprogramar(false);
    setCuotasAdicionales(1);
  };

  const estadoColor = (estado: string) => {
    switch (estado) {
      case 'ACTIVO': return 'primary';
      case 'PAGADO': return 'success';
      case 'ANULADO': return 'error';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    ...(!isMobile ? [
      { field: 'idPrestamo', headerName: 'ID', width: 60 },
    ] : []),
    { field: 'trabajadorNombre', headerName: 'Trabajador', flex: 1, minWidth: isMobile ? 120 : 200,
      valueGetter: (_v: unknown, row: any) => {
        const t = trabajadores.find(tr => tr.idTrabajador === row.idTrabajador);
        return t?.personal?.personaContacto ?? `#${row.idTrabajador}`;
      }},
    ...(!isMobile ? [
      { field: 'tipoPrestamo', headerName: 'Tipo', width: 110 },
      { field: 'descripcion', headerName: 'Descripción', flex: 1, minWidth: 200 },
      { field: 'montoOriginal', headerName: 'Monto Original', width: 130, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
    ] : []),
    { field: 'montoPendiente', headerName: isMobile ? 'Pend.' : 'Pendiente', width: isMobile ? 100 : 120, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2) },
    ...(!isMobile ? [
      { field: 'numeroCuotas', headerName: 'Cuotas', width: 80, type: 'number' as const },
      { field: 'montoCuota', headerName: 'Monto Cuota', width: 120, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
    ] : []),
    { field: 'estado', headerName: 'Estado', width: isMobile ? 90 : 100,
      renderCell: (params) => <Chip label={params.value} size="small" color={estadoColor(params.value as string)} /> },
    ...(!isMobile ? [
      { field: 'fechaInicio', headerName: 'Inicio', width: 110,
        valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
    ] : []),
    { field: 'acciones', headerName: '', width: isMobile ? 80 : 100, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={() => handleVerCuotas(params.row.idPrestamo)} title="Ver cuotas">
            <VerIcon fontSize="small" />
          </IconButton>
          {params.row.estado === 'ACTIVO' && (
            <IconButton size="small" color="error" onClick={() => anularPrestamo(params.row.idPrestamo)} title="Anular">
              <AnularIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  const currentPrestamo = prestamos.find(p => p.idPrestamo === currentPrestamoId);
  const prestamoActivo = currentPrestamo?.estado === 'ACTIVO';

  const cuotaColumns: GridColDef[] = [
    { field: 'numeroCuota', headerName: '#', width: 50 },
    { field: 'monto', headerName: 'Monto', width: 130, type: 'number',
      renderCell: (params) => {
        if (editingCuotaId === params.row.idPrestamoCuota) {
          return (
            <TextField size="small" type="number" value={editMonto}
              onChange={(e) => setEditMonto(Number(e.target.value))}
              sx={{ width: 110 }} inputProps={{ step: '0.01' }} />
          );
        }
        return params.value?.toFixed(2);
      }},
    { field: 'fechaProgramada', headerName: 'Fecha Prog.', width: 150,
      renderCell: (params) => {
        if (editingCuotaId === params.row.idPrestamoCuota) {
          return (
            <TextField size="small" type="date" value={editFecha}
              onChange={(e) => setEditFecha(e.target.value)}
              sx={{ width: 140 }} />
          );
        }
        return params.value ? new Date(params.value).toLocaleDateString('es-PE') : '';
      }},
    { field: 'fechaPago', headerName: 'Fecha Pago', width: 120,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '-' },
    { field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (params) => {
        const color = params.value === 'PAGADA' ? 'success' : params.value === 'ANULADA' ? 'error' : 'warning';
        return <Chip label={params.value} size="small" color={color} />;
      }},
    ...(prestamoActivo ? [{
      field: 'acciones', headerName: '', width: 90, sortable: false,
      renderCell: (params: { row: PrestamoCuota }) => {
        if (params.row.estado !== 'PENDIENTE') return null;
        if (editingCuotaId === params.row.idPrestamoCuota) {
          return (
            <Stack direction="row" spacing={0}>
              <IconButton size="small" color="primary" onClick={handleSaveCuota} title="Guardar">
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setEditingCuotaId(null)} title="Cancelar">
                <CancelIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        }
        return (
          <IconButton size="small" color="primary" onClick={() => handleEditCuota(params.row)} title="Editar cuota">
            <EditIcon fontSize="small" />
          </IconButton>
        );
      },
    } as GridColDef] : []),
  ];

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNew(true)} size={isMobile ? 'small' : 'medium'}>
          {isMobile ? 'Nuevo' : 'Nuevo Préstamo'}
        </Button>
        <Button variant="text" onClick={() => loadPrestamos()} disabled={loading} size={isMobile ? 'small' : 'medium'}>
          {loading ? <CircularProgress size={18} /> : 'Actualizar'}
        </Button>
      </Stack>

      <DataGrid
        rows={prestamos}
        columns={columns}
        getRowId={(row) => row.idPrestamo}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />

      {/* Dialog: Nuevo Préstamo */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuevo Préstamo / Adelanto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={trabajadores}
              getOptionLabel={(t) => t.personal?.personaContacto ?? ''}
              value={selectedTrabajador}
              onChange={(_, val) => {
                setSelectedTrabajador(val);
                setFormData({ ...formData, idTrabajador: val?.idTrabajador ?? 0 });
              }}
              renderInput={(params) => <TextField {...params} label="Buscar trabajador" />}
              isOptionEqualToValue={(opt, val) => opt.idTrabajador === val.idTrabajador}
            />
            <TextField select size="small" label="Tipo" value={formData.tipoPrestamo}
              onChange={(e) => setFormData({ ...formData, tipoPrestamo: e.target.value })}>
              <MenuItem value="ADELANTO">Adelanto</MenuItem>
              <MenuItem value="PRESTAMO">Préstamo</MenuItem>
            </TextField>
            <TextField size="small" label="Descripción" value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} />
            <TextField size="small" label="Monto Original" type="number"
              value={formData.montoOriginal}
              onChange={(e) => setFormData({ ...formData, montoOriginal: Number(e.target.value) })} />
            <TextField size="small" label="Número de Cuotas" type="number"
              value={formData.numeroCuotas}
              onChange={(e) => setFormData({ ...formData, numeroCuotas: Number(e.target.value) })} />
            <TextField size="small" label="Fecha Inicio" type="date"
              value={formData.fechaInicio}
              onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" label="Observaciones" multiline rows={2}
              value={formData.observaciones}
              onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCrear} disabled={loading || !formData.idTrabajador || !formData.montoOriginal}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Ver Cuotas */}
      <Dialog open={openCuotas} onClose={() => setOpenCuotas(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Cuotas del Préstamo</Typography>
            {prestamoActivo && (
              <Button variant="outlined" size="small" startIcon={<ReprogramarIcon />}
                onClick={() => setOpenReprogramar(true)}>
                Agregar Cuotas
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DataGrid
            rows={cuotasSeleccionadas}
            columns={cuotaColumns}
            getRowId={(row: PrestamoCuota) => row.idPrestamoCuota}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCuotas(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Reprogramar Cuotas */}
      <Dialog open={openReprogramar} onClose={() => setOpenReprogramar(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Reprogramar Préstamo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              El monto pendiente se redistribuirá entre las cuotas pendientes actuales más las nuevas cuotas.
            </Typography>
            <TextField size="small" label="Cuotas adicionales" type="number"
              value={cuotasAdicionales}
              onChange={(e) => setCuotasAdicionales(Math.max(1, Number(e.target.value)))}
              inputProps={{ min: 1 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReprogramar(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleReprogramar} disabled={loading || cuotasAdicionales < 1}>
            Reprogramar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
